import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Collapse from '@material-ui/core/Collapse'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Typography from '@material-ui/core/Typography'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import Grid from '@material-ui/core/Grid'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import { NotificationManager } from 'react-notifications'
import Loader from 'components/general/Loader'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import MissingInfoDialog from './MissingInfoDialog'
import { cleanQuillHtml } from 'config/helpers/Helpers'

const useStyles = makeStyles(componentStyles)

const quillModules = {
    toolbar: [
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['bold', 'italic'],
    ]
}

const quillFormats = [
    'list', 'bullet', 'bold', 'italic'
]

function stripHtml(html) {
    // Remove HTML tags and whitespace
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
}

function AboutCompanyCard(props) {
    const classes = { ...useStyles() }
    const [open, setOpen] = useState(true)
    const api = new ProviderAPI()

    const details = props.details || {}
    const about = details.about || ''
    const whyChoose = details.whyChoose || ''
    const customerJourney = details.customerJourney || Array(5).fill('')

    const [loading, setLoading] = useState(false)
    const [showMissingFields, setShowMissingFields] = useState(false)
    const [missingFields, setMissingFields] = useState([])

    function getMissingFields() {
        const fields = [
            { key: 'about', label: 'About', value: about },
            { key: 'whyChoose', label: 'Why Choose', value: whyChoose },
            { key: 'customerJourney', label: 'Customer Journey', value: customerJourney }
        ];
        return fields.filter(f => {
            if (f.key === 'about' || f.key === 'whyChoose') {
                return !stripHtml(f.value || '');
            }
            if (Array.isArray(f.value)) return f.value.filter(Boolean).length === 0;
            return !f.value || f.value.toString().trim() === '';
        });
    }

    async function saveDetails() {
        // If admin, bypass missing field validation entirely
        let proceed = true
        if (!props.admin) {
            const missing = getMissingFields()
            if (missing.length > 0) {
                setMissingFields(missing)
                setShowMissingFields(true)
                proceed = false
            }
        }
        if (!proceed) return

        setLoading(true)
        const details = {
            about: cleanQuillHtml(about),
            whyChoose: cleanQuillHtml(whyChoose),
            customerJourney: customerJourney.filter(Boolean)
        }

        try {
            let company = await api.updateProfileDetailsExtended(props.companyId, details)
            props.updateCompany(company)
            props.updateStage && props.updateStage('counties')
            NotificationManager.success('Details saved')
        } catch (e) {
            onError(e)
        }

        setLoading(false)
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <>
                        <Box display='flex' style={{ justifyContent: 'space-between' }}>
                            <Box>
                                <Box component={Typography} variant='h2' marginBottom='0!important'>
                                    <Box component='span'>Company Bio</Box>
                                </Box>
                                <Box
                                    component='p'
                                    fontSize='.875rem'
                                    marginBottom='0'
                                    marginTop='0'
                                    lineHeight='1.7'
                                    fontWeight='300'
                                >
                                    This is the text that appears on your public profile
                                </Box>
                            </Box>
                        </Box>
                    </>
                }
                onClick={() => setOpen(o => !o)}
                style={{ cursor: 'pointer' }}
                action={<ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginTop: 4 }} />}
                classes={{ root: classes.cardHeaderRoot }}
            ></CardHeader>
            <Collapse in={open}>
            <CardContent>
                <Grid container>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>About {props.name}</FormLabel>
                            <FormControl variant='filled' component={Box} width='100%'>
                                <ReactQuill
                                    theme="snow"
                                    value={about}
                                    onChange={value => props.updateField('profileDetailsExtended.about', value)}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    onFocus={() => props.updateInput && props.updateInput('about')}
                                    style={{ background: '#fff' }}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Why choose {props.name}</FormLabel>
                            <FormControl variant='filled' component={Box} width='100%'>
                                <ReactQuill
                                    theme="snow"
                                    value={whyChoose}
                                    onChange={value => props.updateField('profileDetailsExtended.whyChoose', value)}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    onFocus={() => props.updateInput && props.updateInput('whyChoose')}
                                    style={{ background: '#fff' }}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Customer Journey (each step will appear as a bullet point on your public profile)</FormLabel>
                            {customerJourney.map((step, index) => (
                                <Box key={index} display='flex' alignItems='center' mb={1}>
                                    <FormControl variant='filled' component={Box} width='100%'>
                                        <OutlinedInput
                                            fullWidth
                                            placeholder={`Step ${index + 1}`}
                                            value={step}
                                            onChange={(e) => {
                                                const updated = [...customerJourney]
                                                updated[index] = e.target.value
                                                props.updateField('profileDetailsExtended.customerJourney', updated)
                                            }}
                                            onFocus={() => props.updateInput && props.updateInput('customerJourney')}
                                        />
                                    </FormControl>
                                    {customerJourney.length > 1 && (
                                        <Button
                                            color='primary'
                                            onClick={() => {
                                                const updated = customerJourney.filter((_, i) => i !== index)
                                                props.updateField('profileDetailsExtended.customerJourney', updated)
                                            }}
                                            style={{ marginLeft: 8, minWidth: 36 }}
                                        >
                                            ×
                                        </Button>
                                    )}
                                </Box>
                            ))}
                            <Box mt={1}>
                                <Button
                                    color='primary'
                                    variant='outlined'
                                    onClick={() =>
                                        props.updateField('profileDetailsExtended.customerJourney', [
                                            ...customerJourney,
                                            ''
                                        ])
                                    }
                                >
                                    + Add Step
                                </Button>
                            </Box>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='flex-end' width='100%'>
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={loading}
                                onClick={saveDetails}
                            >
                                {!loading ? <>{(props.admin || props.profile) ? 'Save' : 'Save & Continue'}</> : <Loader height={12} />}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
            </Collapse>
            <MissingInfoDialog
                open={showMissingFields}
                onClose={() => setShowMissingFields(false)}
                missingFields={missingFields}
            />
        </Card>
    )
}

export default AboutCompanyCard